import { clearOnSignOut } from 'src/utils/signedInCaches';
import { reactive } from 'vue';

const ASK_AGAIN_AFTER_MS = 60 * 1000;

/**
 * Values looked up by key and kept until sign-out, for reads that screens repeat: a name, a date,
 * a clinic's payers.
 *
 * A VALUE FOUND IS KEPT; NO VALUE IS ASKED AGAIN after a minute. A failed read and a read that
 * found nothing both leave null, and the db layer reports some failures as nothing, so keeping null
 * would leave a passing failure in place for as long as the page is open. Asking on every call
 * instead would hammer a backend that is down. Reads already out are shared rather than repeated.
 *
 * @template T
 * @param {{read: (key: string) => Promise<T|null>, label: string}} options - `label` names what
 *   failed to read in the log
 * @returns {{get: (key: string) => T|null|undefined, load: (key: string) => Promise<T|null>}}
 *   `get` is reactive: undefined while first read, then the value or null, which stays while it is
 *   asked again. `load` is the same entry as a promise.
 */
// eslint-disable-next-line import/prefer-default-export
export function createLookup({ read, label }) {
  const values = reactive(new Map());
  const pending = new Map();
  const readAt = new Map();
  // A read that lands after sign-out belongs to the user who signed out, and is dropped.
  let session = 0;

  clearOnSignOut(() => {
    session += 1;
    values.clear();
    pending.clear();
    readAt.clear();
  });

  const isSettled = (key) => values.has(key)
    && (values.get(key) !== null || Date.now() - readAt.get(key) < ASK_AGAIN_AFTER_MS);

  const load = (key) => {
    if (pending.has(key)) {
      return pending.get(key);
    }
    if (isSettled(key)) {
      return Promise.resolve(values.get(key));
    }
    const readFor = session;
    const reading = (async () => {
      let found = null;

      try {
        found = (await read(key)) ?? null;
      } catch (error) {
        console.error(`Failed to read ${label}`, error);
      }
      if (readFor === session) {
        values.set(key, found);
        readAt.set(key, Date.now());
        pending.delete(key);
      }
      return found;
    })();

    pending.set(key, reading);
    return reading;
  };

  const get = (key) => {
    load(key);
    return values.get(key);
  };

  return { get, load };
} // createLookup()
